import { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Loader2, Stethoscope, UtensilsCrossed, Wrench, Volume2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { API } from "../lib/site";

const SCENARIOS = [
  {
    id: "dental-booking",
    icon: Stethoscope,
    title: "Dental Clinic Booking",
    subtitle: "Schedules a checkup, answers insurance questions",
    transcript: [
      { who: "caller", text: "Hi, do you have any opening for a dental checkup tomorrow morning?" },
      { who: "agent", text: "Hello! Yes, Dr. Vance has an open slot at 9:30 AM or 11:15 AM. Which works best for you?" },
      { who: "caller", text: "11:15 AM works great. Do you take MetLife insurance?" },
      { who: "agent", text: "We sure do! I've reserved 11:15 AM for you and just sent a confirmation text to your mobile." },
    ],
  },
  {
    id: "restaurant-reservation",
    icon: UtensilsCrossed,
    title: "Trattoria Reservation",
    subtitle: "Books a table, notes dietary requirements",
    transcript: [
      { who: "caller", text: "I'd like to book a table for 4 people tonight at 7 PM." },
      { who: "agent", text: "Good evening! We have a cozy indoor table at 7:00 PM for 4 guests. Any dietary requirements?" },
      { who: "caller", text: "One of us needs gluten-free options." },
      { who: "agent", text: "Perfect, I've noted that for Chef Mario. Your table is confirmed for 7:00 PM tonight." },
    ],
  },
  {
    id: "plumbing-emergency",
    icon: Wrench,
    title: "Emergency Plumbing Dispatch",
    subtitle: "Triages severity, dispatches a technician live",
    transcript: [
      { who: "caller", text: "Help! I have a burst pipe in my basement right now!" },
      { who: "agent", text: "Don't worry — I'm flagging this as Priority 1 dispatch. Please shut off your main water valve. What is your street address?" },
      { who: "caller", text: "424 Elm Street, Austin." },
      { who: "agent", text: "Got it. Technician Mike is 12 minutes away. I've texted you a live tracking link!" },
    ],
  },
];

const BAR_HEIGHTS = [38, 62, 45, 80, 55, 92, 40, 70, 58, 85, 48, 66, 90, 52, 74, 42, 88, 60, 78, 46, 68, 95, 50, 72, 56, 82, 44, 64];

function fmt(t) {
  if (!isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function VoiceDemo() {
  const [active, setActive] = useState(SCENARIOS[0]);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setPlaying(false);
    setTime(0);
    setDuration(0);
    setLoading(false);
    audio.src = `${API}/voice-demo/${active.id}`;
    audio.load();
  }, [active]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setTime(audio.currentTime);
    const onMeta = () => setDuration(audio.duration);
    const onEnd = () => setPlaying(false);
    const onCanPlay = () => setLoading(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("canplay", onCanPlay);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("canplay", onCanPlay);
    };
  }, []);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      setLoading(true);
      try {
        await audio.play();
        setPlaying(true);
      } catch {
        // still buffering
      } finally {
        setLoading(false);
      }
    }
  };

  const seek = (e) => {
    const v = Number(e.target.value);
    if (audioRef.current && duration) {
      audioRef.current.currentTime = (v / 100) * duration;
      setTime(audioRef.current.currentTime);
    }
  };

  const progress = duration ? (time / duration) * 100 : 0;

  const segments = useMemo(() => {
    const weights = active.transcript.map((l) =>
      l.who === "caller" ? l.text.length * 0.25 : l.text.length
    );
    const total = weights.reduce((a, b) => a + b, 0);
    let acc = 0;
    return active.transcript.map((l, i) => {
      const start = acc / total;
      acc += weights[i];
      return { ...l, start, end: acc / total };
    });
  }, [active]);

  const frac = duration ? time / duration : 0;
  const activeLine = playing || time > 0 ? segments.findIndex((s) => frac >= s.start && frac < s.end) : -1;

  return (
    <section id="voice-demo" data-testid="voice-demo-section" className="relative py-24 md:py-36 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 h-[500px] w-[700px] rounded-full bg-violet-600/10 blur-[160px] pointer-events-none" />
      <div className="max-w-7xl mx-auto relative">
        <SectionHeading
          testId="voice-demo-heading"
          kicker="Live audio proof"
          title="Hear your future receptionist"
          sub="Real AI-generated voice. Pick a scenario, press play, and listen to how TalkLogical handles your callers."
        />

        <Reveal delay={0.15} className="mt-12">
          <div className="glow-card rounded-2xl p-6 sm:p-10">
            <div className="flex flex-wrap gap-3" data-testid="voice-demo-scenarios">
              {SCENARIOS.map((s) => (
                <button
                  key={s.id}
                  data-testid={`voice-demo-scenario-${s.id}`}
                  onClick={() => setActive(s)}
                  className={`flex items-center gap-2.5 rounded-full border px-5 py-2.5 text-sm font-semibold transition-[border-color,background-color,color] duration-300 ${
                    active.id === s.id
                      ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200"
                      : "border-white/10 bg-white/5 text-slate-400 hover:border-white/25 hover:text-slate-200"
                  }`}
                >
                  <s.icon className="h-4 w-4" />
                  {s.title}
                </button>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <div className={`flex items-end justify-center gap-1.5 h-28 ${playing ? "eq-playing" : ""}`} data-testid="voice-demo-visualizer">
                  {BAR_HEIGHTS.map((h, i) => (
                    <span
                      key={i}
                      className="eq-bar w-1.5 sm:w-2 rounded-full bg-gradient-to-t from-violet-500 to-cyan-300"
                      style={{ height: `${h}%`, animationDelay: `${(i % 7) * 0.13}s`, animationDuration: `${0.8 + (i % 5) * 0.12}s` }}
                    />
                  ))}
                </div>

                <div className="mt-8 flex items-center gap-5">
                  <button
                    data-testid="voice-demo-play-button"
                    onClick={toggle}
                    className="pulse-ring flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-cyan-400 text-[#07090E] hover:bg-cyan-300 transition-colors"
                    aria-label={playing ? "Pause demo" : "Play demo"}
                  >
                    {loading ? (
                      <Loader2 className="h-6 w-6 animate-spin" />
                    ) : playing ? (
                      <Pause className="h-6 w-6 fill-current" />
                    ) : (
                      <Play className="h-6 w-6 fill-current ml-0.5" />
                    )}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-xs font-mono text-slate-500 mb-2">
                      <span data-testid="voice-demo-current-time">{fmt(time)}</span>
                      <span className="flex items-center gap-1.5">
                        <Volume2 className="h-3.5 w-3.5" />
                        <span data-testid="voice-demo-duration">{fmt(duration)}</span>
                      </span>
                    </div>
                    <input
                      data-testid="voice-demo-seek-bar"
                      type="range"
                      min="0"
                      max="100"
                      value={progress}
                      onChange={seek}
                      className="w-full h-1.5 appearance-none rounded-full bg-white/10 accent-cyan-400 cursor-pointer"
                      aria-label="Seek audio"
                    />
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={active.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="mt-3 text-sm text-slate-400"
                        data-testid="voice-demo-scenario-subtitle"
                      >
                        {active.subtitle}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-[#07090E]/60 p-6" data-testid="voice-demo-transcript">
                <p className="text-xs font-mono font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5">
                  Live transcript
                </p>
                <div className="space-y-4">
                  {segments.map((line, i) => {
                    const isActive = i === activeLine && line.who === "agent";
                    const isPast = activeLine > i;
                    return (
                      <div key={i} className={`flex gap-3 ${line.who === "caller" ? "justify-end" : ""}`}>
                        <div
                          data-testid={`voice-demo-transcript-line-${i}`}
                          className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed transition-[background-color,color,opacity] duration-500 ${
                            line.who === "agent"
                              ? isActive
                                ? "bg-cyan-400/15 text-cyan-100 ring-1 ring-cyan-400/40"
                                : isPast
                                ? "bg-white/5 text-slate-400"
                                : "bg-white/5 text-slate-500"
                              : "bg-violet-500/10 text-slate-400"
                          }`}
                        >
                          <span className={`block text-[10px] font-mono font-semibold tracking-widest uppercase mb-1 ${
                            line.who === "agent" ? "text-cyan-400" : "text-violet-400"
                          }`}>
                            {line.who === "agent" ? "TalkLogical AI" : "Caller"}
                          </span>
                          {line.text}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <audio ref={audioRef} preload="none" data-testid="voice-demo-audio" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
